import { Component, inject, OnInit, signal, OnDestroy, computed } from '@angular/core';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { NewsService, NewsArticle } from '../../../services/news/news.service';
import { CommonModule } from '@angular/common';
import { Subscription } from 'rxjs';

@Component({
  selector: 'app-news',
  imports: [CommonModule, RouterLink],
  templateUrl: './news.html',
  styleUrl: './news.scss',
})
export class News implements OnInit, OnDestroy {
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private newsService = inject(NewsService);

  article = signal<NewsArticle | undefined>(undefined);
  recentArticles = signal<NewsArticle[]>([]);
  private paramSub?: Subscription;

  // --- Lightbox state ---
  lightboxIndex = signal<number | null>(null);
  lightboxOpen = computed(() => this.lightboxIndex() !== null);
  activeImage = computed(() => {
    const idx = this.lightboxIndex();
    const gallery = this.article()?.gallery;
    return (idx !== null && gallery) ? gallery[idx] : null;
  });

  ngOnInit() {
    this.paramSub = this.route.paramMap.subscribe(params => {
      const id = params.get('id');
      if (id) {
        const found = this.newsService.getArticle(id);
        if (found) {
          this.article.set(found);
          this.recentArticles.set(this.newsService.getRecentArticles(id));
          this.lightboxIndex.set(null); // Reset lightbox on navigation
        } else {
          // If article not found, fall back to overview
          this.router.navigate(['/home/overview']);
        }
      }
    });
  }

  ngOnDestroy() {
    this.paramSub?.unsubscribe();
  }

  openLightbox(index: number) {
    this.lightboxIndex.set(index);
  }

  closeLightbox() {
    this.lightboxIndex.set(null);
  }

  prevImage() {
    const idx = this.lightboxIndex();
    const gallery = this.article()?.gallery;
    if (idx !== null && gallery) {
      this.lightboxIndex.set((idx - 1 + gallery.length) % gallery.length);
    }
  }

  nextImage() {
    const idx = this.lightboxIndex();
    const gallery = this.article()?.gallery;
    if (idx !== null && gallery) {
      this.lightboxIndex.set((idx + 1) % gallery.length);
    }
  }

  getCategoryClass(category: NewsArticle['category']): string {
    switch (category) {
      case 'Patch Notes': return 'bg-info text-dark';
      case 'Esports': return 'bg-warning text-dark';
      case 'Community': return 'bg-success text-white';
      default: return 'bg-danger text-white'; // 'New Game'
    }
  }
}
